import React from "react";
import { motion } from "framer-motion";
import { Heart, Ear, Sparkles } from "lucide-react";

const ABOUT_IMAGE = "/kerstin-portrait.jpg";

const values = [
  {
    icon: Ear,
    title: "Aktives Zuhören",
    text: "Ich schenke dir meine ungeteilte Aufmerksamkeit. Ohne zu bewerten, ohne Erwartung.",
  },
  {
    icon: Heart,
    title: "Echte Empathie",
    text: "Ein sicherer Raum, in dem alles sein darf, was gerade da ist.",
  },
  {
    icon: Sparkles,
    title: "Persönliches Wachstum",
    text: "Kreativität als Kompass für Klarheit, Selbstannahme und neue Perspektiven.",
  },
];

export default function About() {
  return (
    <section
      id="ueber-mich"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] rounded-[2rem] overflow-hidden max-w-[420px] mx-auto lg:mx-0">
            <img
              src={ABOUT_IMAGE}
              alt="Kerstin Cloodt, freie Künstlerin und Begleiterin beim Malen in Kassel"
              className="w-full h-full object-cover"
              data-testid="about-image"
            />
          </div>
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-sage/70 -z-0" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-terracotta/70 -z-0" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="text-xs uppercase tracking-[0.28em] text-terracotta">
            Über mich
          </span>
          <h2 className="mt-4 font-serif-elegant text-4xl sm:text-5xl md:text-6xl text-ocean leading-[1.05]">
            Zwischen Leinwand und
            <br />
            <em className="not-italic text-terracotta">Zuhören.</em>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-ink/80 leading-relaxed font-light max-w-2xl">
            <p>
              Mein Name ist <strong className="text-ocean font-medium">Kerstin Cloodt</strong>. Als Begleiterin
              beim Malen und freischaffende Künstlerin in Kassel lebe ich in zwei Welten, die sich tief
              berühren: der stille, forschende Raum der Begegnung und der
              lebendige Ausdruck auf der Leinwand.
            </p>
            <p>
              In meiner Arbeit begleite ich Menschen dabei, sich selbst wieder
              zu spüren. Ohne Leistungsdruck. Ohne den Zwang, „richtig&ldquo; zu
              malen. Nur du, deine Farben und ein Gegenüber, das wirklich
              zuhört.
            </p>
            <p>
              Was ich anbiete, ist keine Methode von der Stange, sondern eine
              ehrliche, authentische Begleitung, genährt von jahrelanger
              Erfahrung und dem tiefen Glauben, dass jede Seele ihren eigenen
              Weg zu Farbe und Form findet.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="p-5 rounded-2xl bg-cream-alt border border-ink/5 hover:border-terracotta/40 transition-colors"
              >
                <v.icon size={22} className="text-terracotta mb-3" strokeWidth={1.5} />
                <div className="font-serif-elegant text-xl text-ocean">{v.title}</div>
                <p className="text-sm text-ink/65 mt-1.5 leading-relaxed">
                  {v.text}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
